import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { CashCustodyStatus } from '@/features/hr-cash-custody/api'
import {
  CustodyList,
  type CustodyListParams,
  type CustodyStatusFilter,
} from '@/features/hr-cash-custody/custody-list'
import { hasGrant } from '@/lib/me'

const STATUSES: CashCustodyStatus[] = ['open', 'submitted', 'closed']

/** Route shell for the cash-custody list and its future detail child. */
export const Route = createFileRoute('/_authenticated/hr/cash-custody')({
  validateSearch: (search: Record<string, unknown>): CustodyListParams => ({
    search: asString(search.search),
    employee: asString(search.employee),
    department_id: asString(search.department_id),
    currency: asString(search.currency),
    from: asString(search.from),
    to: asString(search.to),
    status: asStatus(search.status),
    page: asPage(search.page),
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'cash_custody.view')) {
      throw redirect({ to: '/hr' })
    }
  },
  component: CashCustodyRoute,
})

function CashCustodyRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const search = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <CustodyList
      params={search}
      onParamsChange={(next) =>
        void navigate({ to: '/hr/cash-custody', search: next, replace: true })
      }
    />
  )
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function asStatus(value: unknown): CustodyStatusFilter | undefined {
  return value === 'all' ? 'all' : toStatusArray(value)
}

function toStatusArray(value: unknown): CashCustodyStatus[] | undefined {
  const values = typeof value === 'string' ? [value] : Array.isArray(value) ? value : []
  const statuses = values.filter(
    (item): item is CashCustodyStatus =>
      typeof item === 'string' && STATUSES.includes(item as (typeof STATUSES)[number]),
  )

  return statuses.length > 0 ? statuses : undefined
}

function asPage(value: unknown): number | undefined {
  const page = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : 0
  return Number.isInteger(page) && page > 1 ? page : undefined
}
